/// <amd-module name="Feed/Legacy/Protocols/NuGet/NuGetRestApiUrlGeneratorService" />
import { VssService, IVssService, Services } from "VSS/Platform/Context";
import { replaceRouteValues, Uri } from "VSS/Platform/Util/Url";
import { IRestApiRoutes, generatePackagingServiceRestApiUri } from "Feed/Legacy/Utils/RestApi";

const NuGetArea: string = "nuget";

// Used in Download
const PackageVersionContent: IRestApiRoutes = {
    locationId: "6EA81B8C-7386-490B-A71F-6CF23C80B388",
    resource: "content",
    routeTemplate: "packaging/feeds/{feedId}/{area}/packages/{packageName}/versions/{packageVersion}/{resource}"
};

// Used in CtF, to download nuget + credential provider
const ClientDownload: IRestApiRoutes = {
    locationId: "9E467250-2C61-4278-920B-0C3C618F779F",
    resource: "client",
    routeTemplate: "public/{area}/{resource}/{fileName}" // todo: doesn't seem to be rest, verify
};

// Used in Ctf - Package Source Url
const ServiceIndex: IRestApiRoutes = {
    locationId: "9D3A4E8E-2F8F-4AE1-ABC2-B461A51CB3B3",
    resource: "index.json",
    routeTemplate: "_packaging/{feedId}/{area}/v3/{resource}"
};

// Used for embedded license
const FlatGetFile: IRestApiRoutes = {
    locationId: "76871FBD-8952-415B-9931-A80BC784EBCF",
    resource: "flat2",
    routeTemplate: "_packaging/{feedId}/{area}/v3/{resource}/{id}/{version}/{file}"
};

export interface INuGetRestApiUrlGeneratorService extends IVssService {
    getServiceIndexUrl(feedId: string, includeProject: boolean): string;
    getDownloadPackageVersionContentUrl(
        feedId: string,
        packageName: string,
        packageVersion: string,
        includeProject: boolean
    ): string;
    getDownloadClientUrl(fileName: string, includeProject: boolean);
    getExtractFromNupkgUrl(
        feedId: string,
        packageName: string,
        packageVersion: string,
        innerFilePath: string,
        includeProject: boolean
    ): string;
}

class NuGetRestApiUrlGeneratorService extends VssService implements INuGetRestApiUrlGeneratorService {
    public getExtractFromNupkgUrl(
        feedId: string,
        packageName: string,
        packageVersion: string,
        innerFilePath: string,
        includeProject: boolean
    ): string {
        const path = replaceRouteValues(FlatGetFile.routeTemplate, {
            feedId,
            area: NuGetArea,
            resource: FlatGetFile.resource,
            id: packageName,
            version: packageVersion,
            file: `${packageName}.${packageVersion}.nupkg`,
        });

        const uri = new Uri(
            generatePackagingServiceRestApiUri(
                this.pageContext,
                path,
                true /* ignoreApisRelativePath */,
                includeProject
            )
        );

        uri.addQueryParams({
            extract: innerFilePath,
        });

        return uri.absoluteUri;
    }

    public getServiceIndexUrl(feedId: string, includeProject: boolean = false): string {
        const path = replaceRouteValues(ServiceIndex.routeTemplate, {
            feedId,
            area: NuGetArea,
            resource: ServiceIndex.resource,
        });

        return generatePackagingServiceRestApiUri(
            this.pageContext,
            path,
            true /* ignoreApisRelativePath */,
            includeProject
        );
    }

    public getDownloadPackageVersionContentUrl(
        feedId: string,
        packageName: string,
        packageVersion: string,
        includeProject: boolean = false
    ): string {
        const path = replaceRouteValues(PackageVersionContent.routeTemplate, {
            feedId,
            area: NuGetArea,
            packageName,
            packageVersion,
            resource: PackageVersionContent.resource,
        });

        return generatePackagingServiceRestApiUri(this.pageContext, path, false, includeProject);
    }

    public getDownloadClientUrl(fileName: string, includeProject: boolean): string {
        const path = replaceRouteValues(ClientDownload.routeTemplate, {
            area: NuGetArea,
            resource: ClientDownload.resource,
            fileName,
        });

        return generatePackagingServiceRestApiUri(this.pageContext, path, false, includeProject);
    }
}

Services.add("INuGetRestApiUrlGeneratorService", { serviceFactory: NuGetRestApiUrlGeneratorService });
